import { useEffect, useState } from "react"
import { Modal } from "src/shared/components/layout/modal/Modal"
import IconBorder from "src/shared/components/data-display/IconBorder/IconBorder"
import AuthPinComponent from "src/shared/components/modal/sections/AuthModal/AuthPinComponent"
import LoaderComponent from "src/shared/components/layout/loader/Loader"
import { Button } from "src/shared/components/forms"
import Icon from "src/shared/icons"
import { RoleNames } from "src/shared/hooks/useAuth"
import { PAYMENT_METHODS } from "src/constants/payments"
import { getCurrencyFormat } from "src/utils/string"
import { Metodo } from "./form/Form.types"

type ConfirmacionProps = {
    isOpen: boolean
    loading: boolean
    metodos: Metodo[]
    onAuthConfirm: () => void
    onCancel: () => void
    onClose: () => void
}

const getLabel = ({ tipo_pago, numero_referencia }: Metodo): string => {
    const metodo = Object.values(PAYMENT_METHODS).find((i) => i.value === tipo_pago)
    return `${metodo?.label || tipo_pago}${numero_referencia ? ` *${numero_referencia}` : ""}`
}

function Confirmacion({ isOpen, loading, metodos, onAuthConfirm, onCancel, onClose }: ConfirmacionProps): JSX.Element {
    const [auth, setAuth] = useState<boolean>(false)
    const [showPin, setShowPin] = useState<boolean>(false)
    const propinas = metodos.filter((i) => i.monto_propina > 0)

    useEffect(() => {
        if (!isOpen) setAuth(false)
    }, [isOpen])

    // Se monta el PIN al terminar el deslizamiento para que el foco del input no brinque la animación
    useEffect(() => {
        setShowPin(false)
        if (!auth) return
        const timeout = setTimeout(() => setShowPin(true), 500)
        return () => clearTimeout(timeout)
    }, [auth])

    return (
        <Modal
            className="cortes__edicion-pago__confirm"
            width={570}
            isOpen={isOpen}
            withCloseButton
            withBackButton={auth}
            onBack={() => setAuth(false)}
            onClose={onClose}
            isCancelableOnClickOutside={false}
        >
            <div className="cortes__edicion-pago__content">
                <div
                    className="cortes__edicion-pago__wrapper"
                    style={{ transform: auth ? "translateX(-100%)" : "translateX(0%)" }}
                >
                    <div className="cortes__edicion-pago__item cortes__edicion-pago__confirm-slide">
                        <div className="cortes__edicion-pago__confirm-body">
                            <IconBorder primaryBgColor="var(--fondo-close)" primaryBgDiameter={60}>
                                <Icon name="editFill" color="var(--primary)" width={24} height={24} />
                            </IconBorder>
                            <p className="cortes__edicion-pago__confirm-title">
                                ¿Confirmas la actualización del método de pago?
                            </p>
                            <p className="cortes__edicion-pago__confirm-description">
                                El pago se actualizará con la siguiente distribución:
                            </p>
                            <div className="cortes__edicion-pago__confirm-section">
                                <p className="cortes__edicion-pago__confirm-label">Pago:</p>
                                {metodos.map((metodo) => (
                                    <div className="cortes__edicion-pago__confirm-row" key={metodo.metodo_id}>
                                        <span>{getLabel(metodo)}</span>
                                        <b>{getCurrencyFormat(metodo.subtotal)}</b>
                                    </div>
                                ))}
                            </div>
                            {propinas.length > 0 && (
                                <div className="cortes__edicion-pago__confirm-section">
                                    <p className="cortes__edicion-pago__confirm-label">Propina:</p>
                                    {propinas.map((metodo) => (
                                        <div className="cortes__edicion-pago__confirm-row" key={metodo.metodo_id}>
                                            <span>{getLabel(metodo)}</span>
                                            <b>{getCurrencyFormat(metodo.monto_propina)}</b>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="cortes__edicion-pago__footer cortes__edicion-pago__confirm-footer">
                            <Button
                                type={"button"}
                                theme={"secondary"}
                                className="cortes__edicion-pago__btn"
                                text={"Cancelar"}
                                onClick={onCancel}
                            />
                            <Button
                                type={"button"}
                                className="cortes__edicion-pago__btn"
                                text={"Confirmar actualización"}
                                onClick={() => setAuth(true)}
                            />
                        </div>
                    </div>
                    <div className="cortes__edicion-pago__item cortes__edicion-pago__slide-auth">
                        <IconBorder
                            primaryBgColor="var(--primary)"
                            secondaryBgColor="var(--fondo-close)"
                            primaryBgDiameter={30}
                            secondaryBgDiameter={50}
                            className="cortes__edicion-pago__border"
                        >
                            <Icon name="LockFill" color="var(--white)" width={16} height={16} />
                        </IconBorder>
                        <div className="cortes__edicion-pago__auth-row">
                            <p className="cortes__edicion-pago__auth-title">{"Autorización requerida"}</p>
                            <span className="cortes__edicion-pago__auth-description">
                                Introduce el código de autorización o coloca tu huella en el lector para autorizar el
                                proceso.
                            </span>
                        </div>
                        {showPin && (
                            <AuthPinComponent
                                isOpen={showPin}
                                onLoading={() => null}
                                onAuthFilled={() => onAuthConfirm()}
                                authorizedPins={[RoleNames.superadmin, RoleNames.admin]}
                                authorizedRoles={[RoleNames.superadmin, RoleNames.admin, RoleNames.recepcionista]}
                            />
                        )}
                    </div>
                </div>
            </div>
            <LoaderComponent visible={loading} />
        </Modal>
    )
}

export default Confirmacion
